<template>
  <v-card>
    <v-list>
      <template v-if="sidecarLinks.length > 0">
        <v-subheader>SidecarHR Links</v-subheader>
        <v-list-item v-for="{ href, title, subtitle } in sidecarLinks" :key="href" :href="href" target="_blank">
          <v-list-item-content>
            <v-list-item-title>{{ title }}</v-list-item-title>
            <v-list-item-subtitle v-if="subtitle">{{ subtitle }}</v-list-item-subtitle>
          </v-list-item-content>
        </v-list-item>
        <v-divider />
      </template>
      <v-subheader>
        Banner Links
        <v-spacer />
        <v-switch v-if="isBannerAdminUser" v-model="pprd" :label="pprd ? 'PPRD' : 'PROD'" />
      </v-subheader>
      <v-alert v-if="bannerOffline" type="info">Banner is currently offline doing its daily backup. This occurs daily (except Monday and Saturday) from midnight until 3am.</v-alert>
      <template v-else>
        <v-list-item v-for="({ href, to, title, subtitle }, index) in bannerLinks" :key="'href-' + index" :to="to" :href="href" :target="href ? '_blank' : ''">
          <v-list-item-content>
            <v-list-item-title>{{ title }}</v-list-item-title>
            <v-list-item-subtitle v-if="subtitle">{{ subtitle }}</v-list-item-subtitle>
          </v-list-item-content>
        </v-list-item>
      </template>
      <v-divider />
      <v-list-item href="https://covenantcollege.instructure.com/login/saml" target="_blank">
        <v-list-item-title>Canvas</v-list-item-title>
      </v-list-item>
      <v-divider />
      <v-subheader>Tech Links</v-subheader>
      <v-list-item to="/printers">
        <v-list-item-content>
          <v-list-item-title>Printers</v-list-item-title>
          <v-list-item-subtitle>Install Covenant Printers</v-list-item-subtitle>
        </v-list-item-content>
      </v-list-item>
      <microsoft-dialog></microsoft-dialog>
    </v-list>
  </v-card>
</template>
<script>
import { ref, computed, onMounted, onBeforeUnmount } from '@vue/composition-api'

export default {
  components: {
    MicrosoftDialog: () => import('./banner/microsoftDialog')
  },
  setup (props, { root }) {
    const user = computed(() => 'spoof' in root.$store.state.user ? root.$store.state.user.spoof : root.$store.state.user)
    const roles = computed(() => user.value.roles)
    const isBannerAdminUser = computed(() => roles.value.filter((role) => ['Financial Aid', 'Records', 'Technology Services', 'Accounting', 'Student Development', 'Center for Calling & Career', 'Human Resources', 'Education', 'Office Services', 'Chapel'].includes(role)).length > 0)
    const bannerOffline = ref(true)
    const hasWorkstudy = ref(false)
    const isEmployee = computed(() => roles.value.filter((role) => ['Employee', 'All Staff', 'All Faculty'].includes(role)).length > 0)
    const isStudent = computed(() => roles.value.filter((role) => ['Student', 'Students', 'All Students'].includes(role)).length > 0)
    const sidecarLinks = computed(() => {
      if (!isEmployee.value && !hasWorkstudy.value) return []
      return [{
        href: 'https://lcm-ep.prismhr.com/uex/#/ess/home',
        title: 'SidecarHR Employee Portal'
      },
      {
        href: 'https://wfm.prismhr.com/',
        title: 'SidecarHR Work Force Management (Timeclock)'
      }]
    })
    const bannerLinks = computed(() => {
      const arr = [{
        to: '/links',
        title: 'Banner - Main Menu'
      }]
      if (isStudent.value) {
        arr.push({
          href: 'https://knox01.covenant.edu:8443/StudentSelfService/ssb/studentProfile',
          title: 'Student Profile'
        })
        arr.push({
          href: 'https://knox01.covenant.edu:8443/StudentSelfService/ssb/financialAid',
          title: 'Financial Aid'
        })
      }
      if (roles.value.filter((role) => ['Employee', 'All Staff', 'All Faculty'].includes(role)).length > 0) {
        if (pprd.value) {
          arr.push({
            href: 'https://knox02.covenant.edu:9243/EmployeeSelfService',
            title: 'Employee Profile (PPRD)'
          })
          arr.push({
            href: 'https://knox02.covenant.edu:9243/FinanceSelfService',
            title: 'Banner Finance (PPRD)'
          })
          if (isBannerAdminUser.value) {
            arr.push({
              href: 'https://wycliffe02.covenant.edu:8143/applicationNavigator',
              title: 'Banner Admin (PPRD)'
            })
          }
        } else {
          arr.push({
            href: 'https://knox01.covenant.edu:8443/EmployeeSelfService',
            title: 'Banner Employee Profile'
          })
          arr.push({
            href: 'https://knox01.covenant.edu:8443/FinanceSelfService',
            title: 'Banner Finance'
          })
          if (isBannerAdminUser.value) {
            arr.push({
              href: 'https://wycliffe01.covenant.edu:8443/applicationNavigator',
              title: 'Banner Admin'
            })
          }
        }
      } else if (hasWorkstudy) {
        arr.push({
          href: 'https://knox01.covenant.edu:9543/EmployeeSelfService',
          title: 'Banner Employee Profile'
        })
      }
      return arr
    })

    const interval = ref(null)
    function checkBannerOnline () {
      const dt = new Date()
      const [date, time] = dt.toLocaleString('en', { timeZone: 'America/New_York' }).split(', ')
      const day = new Date(date).getDay()
      const isAm = time.substring(time.length - 2) === 'AM'
      const hour = parseInt(time.substring(0, time.indexOf(':')))
      if (day !== 1 && day !== 6 && isAm && (hour < 3 || hour === 12)) bannerOffline.value = true
      else bannerOffline.value = false
    }

    onMounted(async () => {
      checkBannerOnline()
      // Check to see if they have work-study; if so, then add the Banner Employee Profile linke
      const { pidm } = user.value
      if (roles.value.filter((role) => role === 'Employee' || role === 'All Staff' || role === 'All Faculty').length === 0) {
        const placements = await root.$store.dispatch('work-study/loadPlacements', { pidm })
        if (placements.length > 0) hasWorkstudy.value = true
      }

      interval.value = setInterval(() => checkBannerOnline(), 30000)
    })

    onBeforeUnmount(() => {
      clearInterval(interval.value)
    })

    const pprd = ref(false)

    return {
      user,
      roles,
      isBannerAdminUser,
      bannerOffline,
      sidecarLinks,
      bannerLinks,
      interval,
      checkBannerOnline,
      pprd
    }
  }
}
</script>
